import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  DocumentCheckIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { engineerService } from '../../services/engineerService';
import { PageHeader, Button, Badge, FormField, TextInput, TextArea, Select } from '../../components/ui';
import SignaturePad from './SignaturePad';

type PriceBookPayload = Awaited<ReturnType<typeof engineerService.getPriceBook>>;
type PriceBookItem = PriceBookPayload['items'][number];
type QuotePolicy = PriceBookPayload['policy'];

interface LineDraft {
  key: string;
  priceBookItemId?: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  vatApplies: boolean;
  custom?: boolean;
  minPrice?: number | null;
  maxPrice?: number | null;
  unit?: string;
}

const genKey = () => Math.random().toString(36).slice(2, 9);

const EngineerQuoteBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselectJobId = params.get('jobId') || null;

  const [step, setStep] = useState<'job' | 'items' | 'signoff'>(preselectJobId ? 'items' : 'job');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(preselectJobId);
  const [jobTitle, setJobTitle] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [sitePostcode, setSitePostcode] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: jobs = [], isLoading: jobsLoading } = useQuery({
    queryKey: ['engineer', 'quote-builder', 'jobs'],
    queryFn: () => engineerService.listJobs({ limit: 50 }),
  });

  const { data: priceBook, isLoading: priceBookLoading } = useQuery({
    queryKey: ['engineer', 'price-book'],
    queryFn: () => engineerService.getPriceBook(),
  });

  const policy: QuotePolicy | undefined = priceBook?.policy;
  const selectedJob = useMemo(
    () => jobs.find((j) => j.id === selectedJobId) || null,
    [jobs, selectedJobId],
  );

  // Autofill from selected job
  useEffect(() => {
    if (!selectedJob) return;
    setJobTitle((t) => t || selectedJob.title || '');
    setDescription((d) => d || selectedJob.description || '');
    setSiteAddress((a) => a || selectedJob.address || selectedJob.client?.address || '');
    setSitePostcode((p) => p || selectedJob.postcode || selectedJob.client?.postcode || '');
    setCustomerName((n) => n || selectedJob.client?.name || '');
  }, [selectedJob]);

  const vatRatePct = (priceBook as any)?.vatRate ?? 20;
  const subtotal = lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0);
  const vatAmount = lines.reduce(
    (s, l) => s + (l.vatApplies ? l.quantity * l.unitPrice : 0),
    0,
  ) * 0.2;
  const total = +(subtotal + vatAmount).toFixed(2);

  const willNeedAdmin = useMemo(() => {
    if (!policy) return false;
    if (policy.model === 'ALWAYS_ADMIN') return true;
    if (policy.model === 'TRUST') return false;
    const threshold = policy.thresholdAmount ?? 500;
    return total > threshold;
  }, [policy, total]);

  const addPriceBookItem = (item: PriceBookItem) => {
    setLines((ls) => [
      ...ls,
      {
        key: genKey(),
        priceBookItemId: item.id,
        description: item.name,
        quantity: 1,
        unitPrice: item.unitPrice,
        vatApplies: item.vatApplies !== false,
        custom: false,
        minPrice: item.minPrice ?? null,
        maxPrice: item.maxPrice ?? null,
        unit: item.unit,
      },
    ]);
    setCatalogOpen(false);
  };

  const addCustomItem = () => {
    if (policy && policy.allowCustomItems === false) {
      setError('Custom items are not allowed by company policy.');
      return;
    }
    setLines((ls) => [
      ...ls,
      {
        key: genKey(),
        priceBookItemId: null,
        description: '',
        quantity: 1,
        unitPrice: 0,
        vatApplies: true,
        custom: true,
      },
    ]);
  };

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const removeLine = (key: string) => {
    setLines((ls) => ls.filter((l) => l.key !== key));
  };

  const goItems = () => {
    if (!selectedJobId) {
      setError('Please pick a job first.');
      return;
    }
    setError(null);
    setStep('items');
  };

  const goSignoff = () => {
    if (lines.length === 0) {
      setError('Add at least one item.');
      return;
    }
    for (const l of lines) {
      if (!l.description.trim()) {
        setError('Every line needs a description.');
        return;
      }
      if (l.quantity <= 0) {
        setError('Every line needs quantity > 0.');
        return;
      }
      if (l.priceBookItemId) {
        if (l.minPrice != null && l.unitPrice < l.minPrice) {
          setError(`Unit price is below the allowed minimum (£${l.minPrice}) for "${l.description}".`);
          return;
        }
        if (l.maxPrice != null && l.unitPrice > l.maxPrice) {
          setError(`Unit price is above the allowed maximum (£${l.maxPrice}) for "${l.description}".`);
          return;
        }
      }
    }
    setError(null);
    setStep('signoff');
  };

  const submit = async () => {
    if (!selectedJob || !selectedJob.client?.id) {
      setError('Job is missing a client — cannot submit.');
      return;
    }
    if (!willNeedAdmin && policy?.requireCustomerSignature && !signatureDataUrl) {
      setError('A customer signature is required before submitting.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let signatureUrl: string | undefined;
      if (signatureDataUrl) {
        try {
          const res = await engineerService.uploadCertAsset('signature', signatureDataUrl, 'image/png');
          signatureUrl = res.signedUrl || res.path;
        } catch {
          signatureUrl = signatureDataUrl;
        }
      }

      const quote = await engineerService.createQuote({
        clientId: selectedJob.client.id,
        jobId: selectedJob.id,
        jobTitle: jobTitle.trim(),
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
        siteAddress: siteAddress.trim() || undefined,
        sitePostcode: sitePostcode.trim() || undefined,
        customerName: customerName.trim() || undefined,
        signatureUrl,
        items: lines.map((l) => ({
          priceBookItemId: l.priceBookItemId || undefined,
          description: l.description.trim(),
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          custom: l.custom,
        })),
      });

      navigate(`/engineer/quotes/${quote.id}`, { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to submit quote.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="New quote"
        subtitle="Build a quick quote for the customer on site."
        actions={
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeftIcon className="w-4 h-4" />}
            onClick={() => navigate('/engineer/quotes')}
          >
            Back
          </Button>
        }
      />

      <Stepper step={step} />

      {step === 'job' && (
        <StepCard
          title="Which job is this quote for?"
          subtitle="Pick one of your jobs to autofill the customer and site details."
        >
          {jobsLoading ? (
            <div className="text-sm text-slate-500">Loading your jobs…</div>
          ) : jobs.length === 0 ? (
            <div className="text-sm text-slate-500">
              You don't have any jobs assigned yet.
            </div>
          ) : (
            <div className="space-y-2">
              {jobs.map((j) => (
                <button
                  key={j.id}
                  type="button"
                  onClick={() => setSelectedJobId(j.id)}
                  className={[
                    'w-full text-left rounded-lg border p-3 transition',
                    selectedJobId === j.id
                      ? 'border-slate-900 ring-1 ring-slate-900 bg-slate-50'
                      : 'border-slate-200 hover:border-slate-300 bg-white',
                  ].join(' ')}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">
                      {j.title}
                    </div>
                    <Badge tone="neutral">{j.status}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {j.client?.name || 'No client'}
                    {j.scheduledDate && (
                      <> · {new Date(j.scheduledDate).toLocaleDateString()}</>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          <div className="flex justify-end pt-3">
            <Button onClick={goItems} disabled={!selectedJobId}>
              Continue
            </Button>
          </div>
        </StepCard>
      )}

      {step === 'items' && (
        <StepCard
          title="Items & pricing"
          subtitle="Add items from your company's price book or a custom line."
        >
          {priceBookLoading ? (
            <div className="text-sm text-slate-500">Loading price book…</div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <FormField label="Job title" required>
                  <TextInput
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                  />
                </FormField>
                <FormField label="Customer name">
                  <TextInput
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                </FormField>
              </div>
              <FormField label="Scope / description">
                <TextArea
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Briefly describe the work the customer is quoting for."
                />
              </FormField>

              <div className="flex items-center justify-between mt-4">
                <h3 className="text-sm font-semibold text-slate-900">Line items</h3>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                    onClick={() => setCatalogOpen(true)}
                  >
                    Price book
                  </Button>
                  {policy?.allowCustomItems !== false && (
                    <Button
                      size="sm"
                      leftIcon={<PlusIcon className="w-4 h-4" />}
                      onClick={addCustomItem}
                    >
                      Custom line
                    </Button>
                  )}
                </div>
              </div>

              {lines.length === 0 ? (
                <div className="mt-2 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                  No items yet. Add from the price book or a custom line.
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  {lines.map((l) => (
                    <LineRow
                      key={l.key}
                      line={l}
                      onChange={(patch) => updateLine(l.key, patch)}
                      onRemove={() => removeLine(l.key)}
                    />
                  ))}
                </div>
              )}

              <Totals
                subtotal={subtotal}
                vatAmount={vatAmount}
                total={total}
                vatRatePct={vatRatePct}
              />

              {error && (
                <div className="text-xs text-red-600 pt-2" role="alert">
                  {error}
                </div>
              )}
              <div className="flex justify-between pt-3">
                <Button variant="ghost" onClick={() => setStep('job')}>
                  Back
                </Button>
                <Button onClick={goSignoff}>Continue to sign-off</Button>
              </div>
            </>
          )}
        </StepCard>
      )}

      {step === 'signoff' && (
        <StepCard
          title="Review & sign off"
          subtitle={
            willNeedAdmin
              ? 'This total is above your auto-approve threshold — it will be sent to admins for review.'
              : policy?.requireCustomerSignature
              ? 'Capture the customer signature to accept the quote on the spot.'
              : 'Optionally capture a customer signature. Otherwise the quote will be sent as-is.'
          }
        >
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Job</span>
              <span className="font-medium text-slate-900">{jobTitle}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Customer</span>
              <span className="font-medium text-slate-900">
                {customerName || selectedJob?.client?.name || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Items</span>
              <span className="font-medium text-slate-900">{lines.length}</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
              <span className="font-medium text-slate-900">Total</span>
              <span className="font-semibold text-slate-900">£{total.toFixed(2)}</span>
            </div>
          </div>

          <FormField label="Notes (visible on the quote)">
            <TextArea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes for the customer."
            />
          </FormField>

          {willNeedAdmin ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <DocumentCheckIcon className="w-4 h-4 inline mr-1" />
              Admin approval required — the customer will be emailed once approved.
            </div>
          ) : (
            <FormField label="Customer signature">
              <SignaturePad
                value={signatureDataUrl}
                onChange={setSignatureDataUrl}
              />
            </FormField>
          )}

          {error && (
            <div className="text-xs text-red-600 pt-2" role="alert">
              {error}
            </div>
          )}
          <div className="flex justify-between pt-3">
            <Button variant="ghost" onClick={() => setStep('items')}>
              Back
            </Button>
            <Button onClick={submit} loading={submitting}>
              {willNeedAdmin ? 'Submit for approval' : 'Submit quote'}
            </Button>
          </div>
        </StepCard>
      )}

      {catalogOpen && priceBook && (
        <PriceBookPicker
          priceBook={priceBook}
          onPick={addPriceBookItem}
          onClose={() => setCatalogOpen(false)}
        />
      )}
    </div>
  );
};

const Stepper: React.FC<{ step: 'job' | 'items' | 'signoff' }> = ({ step }) => {
  const steps: Array<{ key: typeof step; label: string }> = [
    { key: 'job', label: 'Job' },
    { key: 'items', label: 'Items' },
    { key: 'signoff', label: 'Sign-off' },
  ];
  const currentIdx = steps.findIndex((s) => s.key === step);
  return (
    <ol className="flex items-center gap-2 text-xs">
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <li
            className={[
              'flex items-center gap-1.5 rounded-full px-2.5 py-1',
              i <= currentIdx
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-500',
            ].join(' ')}
          >
            <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[10px]">
              {i + 1}
            </span>
            {s.label}
          </li>
          {i < steps.length - 1 && (
            <span className="flex-1 h-px bg-slate-200" aria-hidden />
          )}
        </React.Fragment>
      ))}
    </ol>
  );
};

const StepCard: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
    <div className="px-5 py-4 border-b border-slate-100">
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
    <div className="p-5 space-y-3">{children}</div>
  </div>
);

const LineRow: React.FC<{
  line: LineDraft;
  onChange: (patch: Partial<LineDraft>) => void;
  onRemove: () => void;
}> = ({ line, onChange, onRemove }) => {
  const lineTotal = line.quantity * line.unitPrice;
  const belowMin = line.minPrice != null && line.unitPrice < line.minPrice;
  const aboveMax = line.maxPrice != null && line.unitPrice > line.maxPrice;
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2">
      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-2">
          <TextInput
            value={line.description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder={line.custom ? 'Custom item description' : 'Description'}
          />
          <div className="grid grid-cols-3 gap-2">
            <FormField label="Qty">
              <TextInput
                type="number"
                step="1"
                min={0.01}
                value={line.quantity}
                onChange={(e) => onChange({ quantity: parseFloat(e.target.value) || 0 })}
              />
            </FormField>
            <FormField label={`Unit price${line.unit ? ` (/ ${line.unit})` : ''}`}>
              <TextInput
                type="number"
                step="0.01"
                min={0}
                value={line.unitPrice}
                onChange={(e) => onChange({ unitPrice: parseFloat(e.target.value) || 0 })}
              />
            </FormField>
            <FormField label="Line total">
              <div className="h-9 flex items-center px-2 rounded-md bg-slate-50 border border-slate-200 text-sm">
                £{lineTotal.toFixed(2)}
              </div>
            </FormField>
          </div>
          {belowMin && (
            <div className="text-[11px] text-red-600">
              Below allowed minimum (£{line.minPrice}).
            </div>
          )}
          {aboveMax && (
            <div className="text-[11px] text-red-600">
              Above allowed maximum (£{line.maxPrice}).
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="p-2 text-slate-400 hover:text-red-600"
          aria-label="Remove line"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const Totals: React.FC<{
  subtotal: number;
  vatAmount: number;
  total: number;
  vatRatePct: number;
}> = ({ subtotal, vatAmount, total, vatRatePct }) => (
  <div className="rounded-lg bg-slate-50 p-3 text-sm space-y-1">
    <div className="flex justify-between">
      <span className="text-slate-600">Subtotal</span>
      <span className="font-medium text-slate-900">£{subtotal.toFixed(2)}</span>
    </div>
    <div className="flex justify-between">
      <span className="text-slate-600">VAT ({vatRatePct}%)</span>
      <span className="font-medium text-slate-900">£{vatAmount.toFixed(2)}</span>
    </div>
    <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
      <span className="font-semibold text-slate-900">Total</span>
      <span className="font-semibold text-slate-900">£{total.toFixed(2)}</span>
    </div>
  </div>
);

const PriceBookPicker: React.FC<{
  priceBook: PriceBookPayload;
  onPick: (item: PriceBookItem) => void;
  onClose: () => void;
}> = ({ priceBook, onPick, onClose }) => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('all');

  const items = useMemo(() => {
    const s = search.trim().toLowerCase();
    return priceBook.items
      .filter((i) => {
        if (category === 'all') return true;
        if (category === 'none') return !i.categoryId;
        return i.categoryId === category;
      })
      .filter((i) =>
        !s
          ? true
          : i.name.toLowerCase().includes(s) ||
            (i.description || '').toLowerCase().includes(s) ||
            (i.sku || '').toLowerCase().includes(s),
      );
  }, [priceBook.items, search, category]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white sm:rounded-xl shadow-xl w-full sm:max-w-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold">Add from price book</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-3 border-b border-slate-100 flex gap-2">
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items…"
          />
          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="all">All</option>
            <option value="none">Uncategorised</option>
            {priceBook.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex-1 overflow-auto">
          {items.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-10">
              No matching items.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onPick(item)}
                    className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {item.name}
                      </div>
                      {item.description && (
                        <div className="text-xs text-slate-500 truncate">
                          {item.description}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-slate-900">
                        £{item.unitPrice.toFixed(2)}
                      </div>
                      <div className="text-[11px] text-slate-500">/ {item.unit}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default EngineerQuoteBuilder;
